<template>
  <div
    role="main"
    aria-label="Tableau des gouvernorats"
    tabindex="-1"
  >
    <b-row
      class="match-height mb-2"
      aria-label="Statistiques logistiques globales"
    >
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-primary">
          <div class="mb-1">
            <feather-icon
              icon="MapIcon"
              size="21"
              class="gov-text-purple"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ governorates.length || 0 }}
          </h2>
          <p class="card-text gov-text-purple">
            Total des gouvernorats
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-success">
          <div class="mb-1">
            <feather-icon
              icon="CheckCircleIcon"
              size="21"
              class="text-success"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ activeCount }}
          </h2>
          <p class="card-text text-success">
            Gouvernorats actifs
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-warning">
          <div class="mb-1">
            <feather-icon
              icon="PackageIcon"
              size="21"
              class="text-warning"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ totalDelegations }}
          </h2>
          <p class="card-text text-warning">
            Total délégations
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-info">
          <div class="mb-1">
            <feather-icon
              icon="MapPinIcon"
              size="21"
              class="text-info"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ totalLocalities }}
          </h2>
          <p class="card-text text-info">
            Total localités
          </p>
        </b-card>
      </b-col>
    </b-row>

    <!-- Advanced filters -->
    <b-card
      class="mb-2"
      aria-label="Filtres avancés pour la gestion des gouvernorats"
    >
      <b-card-title id="filters-title">
        Filtres avancés
      </b-card-title>
      <span class="sr-only">Utilisez ces filtres pour affiner la recherche des gouvernorats selon des critères logistiques professionnels.</span>
      <b-row>
        <!-- Basic filters -->
        <b-col
          md="6"
          sm="12"
        >
          <b-form-group
            label="Nom du gouvernorat"
            label-for="filter-name"
          >
            <b-form-input
              id="filter-name"
              v-model="filters.name"
              v-debounce:500ms="getGovernorates"
              placeholder="Rechercher par nom officiel du gouvernorat"
              class="filter-input"
              aria-describedby="filter-name-help"
            />
            <small
              id="filter-name-help"
              class="form-text text-muted"
            >Saisissez le nom officiel pour une recherche précise.</small>
          </b-form-group>
        </b-col>
        <b-col
          md="6"
          sm="12"
        >
          <b-form-group
            label="État logistique"
            label-for="filter-etat"
          >
            <v-select
              id="filter-etat"
              v-model="filters.active"
              :options="statusOptions"
              placeholder="Tous"
              :clearable="true"
              label="label"
              class="select-search-option"
              :reduce="option => option.value"
              aria-describedby="filter-etat-help"
              @input="getGovernorates"
            />
            <small
              id="filter-etat-help"
              class="form-text text-muted"
            >Filtrez selon l’état logistique (actif/inactif).</small>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Additional filters (toggleable) -->
      <div v-if="showMoreFilters">
        <b-row>
          <!-- Delegation count range -->
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group
              label="Nombre de délégations logistiques"
              label-for="filter-delegations"
            >
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="filters.minDelegations"
                  v-debounce:500ms="getGovernorates"
                  placeholder="Min"
                  type="number"
                  min="0"
                  class="mr-2 filter-input"
                />
                <span class="mx-1">à</span>
                <b-form-input
                  v-model="filters.maxDelegations"
                  v-debounce:500ms="getGovernorates"
                  placeholder="Max"
                  type="number"
                  min="0"
                  class="ml-2 filter-input"
                />
              </div>
            </b-form-group>
          </b-col>

          <!-- Locality count range -->
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group
              label="Nombre de localités desservies"
              label-for="filter-localities"
            >
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="filters.minLocalities"
                  v-debounce:500ms="getGovernorates"
                  placeholder="Min"
                  type="number"
                  min="0"
                  class="mr-2 filter-input"
                />
                <span class="mx-1">à</span>
                <b-form-input
                  v-model="filters.maxLocalities"
                  v-debounce:500ms="getGovernorates"
                  placeholder="Max"
                  type="number"
                  min="0"
                  class="ml-2 filter-input"
                />
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <!-- Date filters -->
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group
              label="Date de création du gouvernorat"
              label-for="filter-date"
            >
              <b-row>
                <b-col>
                  <b-form-datepicker
                    v-model="filters.createdAfter"
                    placeholder="À partir de"
                    locale="fr"
                    class="mb-2 filter-input"
                    @input="getGovernorates"
                  />
                </b-col>
                <b-col>
                  <b-form-datepicker
                    v-model="filters.createdBefore"
                    placeholder="Jusqu'à"
                    locale="fr"
                    class="filter-input"
                    @input="getGovernorates"
                  />
                </b-col>
              </b-row>
            </b-form-group>
          </b-col>

          <!-- Avec localités filter -->
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group
              label="Présence de localités desservies"
              label-for="filter-haslocalities"
            >
              <v-select
                v-model="filters.hasLocalities"
                :options="hasLocalitiesOptions"
                placeholder="Tous"
                :clearable="true"
                label="label"
                class="select-search-option"
                :reduce="option => option.value"
                @input="getGovernorates"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <div class="d-flex justify-content-between mt-1">
        <div>
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="resetFilters"
          >
            <span>Réinitialiser</span>
          </b-button>
        </div>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="toggleMoreFilters"
        >
          <span>{{ showMoreFilters ? 'Moins de filtres' : 'Plus de filtres' }}</span>
        </b-button>
      </div>
    </b-card>

    <!-- input Recherche and Add button -->
    <div
      class="custom-search d-flex justify-content-between mb-2"
      aria-label="Barre d’outils de gestion des gouvernorats"
    >
      <div class="d-flex align-items-center">
        <h3
          id="gov-table-title"
          class="mb-0 mr-2"
        >
          Liste des Gouvernorats logistiques
        </h3>
        <b-badge
          variant="primary"
          pill
          class="badge-round"
        >
          {{ governorates.length || 0 }}
        </b-badge>
      </div>
      <div class="d-flex">
        <b-form-group
          v-if="user.role==='admin'"
          class="mb-0"
        >
          <router-link
            :to="{ name: 'governorate-add' }"
            class="btn btn-primary d-inline-block"
          >
            <feather-icon icon="PlusIcon" />
            <span class="ml-1">Ajouter gouvernorat</span>
          </router-link>
        </b-form-group>
      </div>
    </div>

    <div
      v-if="isLoading === true"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
    </div>

    <!-- table -->
    <vue-good-table
      v-else
      :columns="columns"
      :rows="governorates"
      :pagination-options="{ enabled: false }"
      aria-label="Tableau des gouvernorats logistiques"
      role="table"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- Enhanced name column with link -->
        <span v-if="props.column.field === 'name'">
          <router-link
            :to="{ name: 'governorate-detail', params: { id: props.row.id }}"
            class="font-weight-bold text-primary"
          >
            {{ props.row.name }}
          </router-link>
        </span>

        <span v-else-if="props.column.field === 'is_active'">
          <div class="status-cell">
            <transition
              name="fade"
              mode="out-in"
            >
              <div
                v-if="props.row.is_active === true"
                key="active"
                class="d-flex flex-column align-items-center"
              >
                <b-badge
                  class="badge-glow bg-success mb-1 status-badge"
                  pill
                >
                  <feather-icon
                    icon="CheckIcon"
                    class="mr-25"
                    size="12"
                  />
                  <span>Activé</span>
                </b-badge>
              </div>
              <div
                v-else
                key="inactive"
                class="d-flex flex-column align-items-center"
              >
                <b-badge
                  class="badge-glow bg-danger mb-1 status-badge"
                  pill
                >
                  <feather-icon
                    icon="XIcon"
                    class="mr-25"
                    size="12"
                  />
                  <span>Désactivé</span>
                </b-badge>
              </div>
            </transition>
          </div>
        </span>

        <span v-else-if="props.column.field === 'action'">
          <span>
            <router-link
              v-b-tooltip.hover
              class="text-info mr-1"
              :to="{ name: 'governorate-detail', params: { id: props.row.id }}"
              title="Voir détails"
            >
              <feather-icon
                icon="EyeIcon"
                class="cursor-pointer"
                size="16"
              />
            </router-link>

            <router-link
              v-if="isAdmin"
              v-b-tooltip.hover
              class="text-warning mr-1"
              :to="{ name: 'governorate-edit', params: { id: props.row.id }}"
              title="Modifier"
            >
              <feather-icon
                icon="EditIcon"
                class="cursor-pointer"
                size="16"
              />
            </router-link>

            <b-button
              v-if="isAdmin"
              v-b-tooltip.hover
              variant="link"
              class="btn-icon p-0"
              :disabled="toggleStatusLoading === props.row.id"
              :title="props.row.is_active ? 'Désactiver ce gouvernorat' : 'Activer ce gouvernorat'"
              @click="confirmToggleStatus(props.row.id, !props.row.is_active)"
            >
              <b-spinner
                v-if="toggleStatusLoading === props.row.id"
                small
                type="grow"
              />
              <feather-icon
                v-else
                :icon="props.row.is_active ? 'XIcon' : 'CheckIcon'"
                :class="props.row.is_active ? 'text-danger' : 'text-success'"
                size="18"
              />
            </b-button>
          </span>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- Empty state template -->
      <template slot="emptystate">
        <div
          class="empty-state text-center py-5"
          aria-live="polite"
          role="status"
        >
          <feather-icon
            icon="MapIcon"
            size="48"
            class="text-muted mb-2"
          />
          <h4>Aucun gouvernorat logistique trouvé</h4>
          <p class="text-muted">
            Aucun gouvernorat ne correspond à vos critères de recherche logistique.
          </p>
          <div v-if="hasFilters">
            <p>Essayez de modifier vos filtres ou réinitialisez-les pour élargir la recherche.</p>
            <b-button
              variant="outline-primary"
              size="sm"
              aria-label="Réinitialiser les filtres logistiques"
              @click="resetFilters"
            >
              Réinitialiser les filtres
            </b-button>
          </div>
        </div>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap d-none d-sm-inline">
              Affichage
            </span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              @input="(value)=>props.perPageChanged({currentPerPage:value})"
            />
            <span class="text-nowrap"> de {{ props.total }} résultat{{ props.total > 1 ? 's' : '' }} trouvé{{ props.total > 1 ? 's' : '' }} </span>
          </div>
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value)=>props.pageChanged({currentPage:value})"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

    <!-- Status confirmation modal -->
    <b-modal
      id="confirm-status-modal"
      ref="confirmStatusModal"
      :title="statusModalTitle"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      @ok="handleStatusConfirmed"
      @cancel="cancelStatusChange"
    >
      <p>{{ statusModalMessage }}</p>
    </b-modal>
  </div>
</template>

<script>
import {
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BButton,
  BSpinner,
  BCard,
  BCardTitle,
  BRow,
  BCol,
  BBadge,
  BFormDatepicker,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'

export default {
  components: {
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BSpinner,
    BCard,
    BCardTitle,
    BRow,
    BCol,
    BBadge,
    BFormDatepicker,
    vSelect,
  },
  data() {
    return {
      isLoading: false,
      pageLength: 100,
      user: {},
      columns: [
        {
          label: 'Governorat',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'chercher governorat',
          },
        },
        {
          label: 'État',
          field: 'is_active',
          filterOptions: {
            enabled: true,
          },
          thClass: 'text-center min-width-150',
          tdClass: 'text-center min-width-150',
        },
        {
          label: 'N° des delegations',
          field: 'num_delegations',
          filterOptions: {
            enabled: true,
            placeholder: 'chercher nombre des delegations',
          },
        },
        {
          label: 'N° localités',
          field: 'num_localities',
          filterOptions: {
            enabled: true,
            placeholder: 'chercher nombre des localités',
          },
        },
        {
          label: 'Action',
          field: 'action',
        },
      ],
      governorates: [],
      showMoreFilters: false,
      toggleStatusLoading: null,
      statusModalTitle: '',
      statusModalMessage: '',
      governorateToToggle: null,
      newStatus: null,

      // Advanced filters
      filters: {
        name: '',
        active: null,
        hasDelegations: null,
        hasLocalities: null,
        minDelegations: null,
        maxDelegations: null,
        minLocalities: null,
        maxLocalities: null,
        createdAfter: null,
        createdBefore: null,
      },

      // Filter options
      statusOptions: [
        { value: true, label: 'Actif' },
        { value: false, label: 'Inactif' },
      ],
      hasDelegationsOptions: [
        { value: true, label: 'Oui' },
        { value: false, label: 'Non' },
      ],
      hasLocalitiesOptions: [
        { value: true, label: 'Oui' },
        { value: false, label: 'Non' },
      ],

      // Add count variables
      activeCount: 0,
      totalDelegations: 0,
      totalLocalities: 0,

      // Add the missing pagination properties
      currentPage: 1,
      totalRows: 0,
      perPage: 10,
    }
  },

  computed: {
    isAdmin() {
      return this.user.role === 'admin'
    },

    hasFilters() {
      return this.filters.name !== ''
        || this.filters.active !== null
        || this.filters.hasDelegations !== null
        || this.filters.hasLocalities !== null
        || this.filters.minDelegations !== null
        || this.filters.maxDelegations !== null
        || this.filters.minLocalities !== null
        || this.filters.maxLocalities !== null
        || this.filters.createdAfter !== null
        || this.filters.createdBefore !== null
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.getGovernorates()
  },
  methods: {
    // Toggle more filters
    toggleMoreFilters() {
      this.showMoreFilters = !this.showMoreFilters
    },

    // get Gouvernorats with optional filters
    async getGovernorates() {
      this.isLoading = true
      try {
        let url = '/api/governorates/list/'
        const params = new URLSearchParams()
        // Filters only, no pagination
        if (this.filters.name) params.append('name__icontains', this.filters.name)
        if (this.filters.active !== null) params.append('is_active', this.filters.active)
        if (this.filters.hasDelegations !== null) params.append('has_delegations', this.filters.hasDelegations)
        if (this.filters.hasLocalities !== null) params.append('has_localities', this.filters.hasLocalities)
        if (this.filters.minDelegations !== null && this.filters.minDelegations !== '') {
          params.append('min_delegations', parseInt(this.filters.minDelegations, 10))
        }
        if (this.filters.maxDelegations !== null && this.filters.maxDelegations !== '') {
          params.append('max_delegations', parseInt(this.filters.maxDelegations, 10))
        }
        if (this.filters.minLocalities !== null && this.filters.minLocalities !== '') {
          params.append('min_localities', parseInt(this.filters.minLocalities, 10))
        }
        if (this.filters.maxLocalities !== null && this.filters.maxLocalities !== '') {
          params.append('max_localities', parseInt(this.filters.maxLocalities, 10))
        }
        if (this.filters.createdAfter) {
          params.append('created_after', this.formatDate(this.filters.createdAfter))
        }
        if (this.filters.createdBefore) {
          params.append('created_before', this.formatDate(this.filters.createdBefore))
        }
        // Build URL
        if (params.toString()) url = `${url}?${params.toString()}`
        const { data } = await axios.get(url)
        // Expect a list, not paginated
        this.governorates = Array.isArray(data) ? data : (data.results || [])
        this.totalRows = this.governorates.length
        this.calculateStatistics()
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des gouvernorats')
      } finally {
        this.isLoading = false
      }
    },

    // Format date for API requests
    formatDate(date) {
      if (!date) return null

      // Convert to YYYY-MM-DD format
      if (typeof date === 'string' && date.includes('-')) {
        return date // Already in correct format
      }

      const d = new Date(date)
      let month = `${d.getMonth() + 1}`
      let day = `${d.getDate()}`
      const year = d.getFullYear()

      if (month.length < 2) month = `0${month}`
      if (day.length < 2) day = `0${day}`

      return [year, month, day].join('-')
    },

    // Apply filters
    applyFilters() {
      this.getGovernorates()
    },

    // Reset filters
    resetFilters() {
      this.filters = {
        name: '',
        active: null,
        hasDelegations: null,
        hasLocalities: null,
        minDelegations: null,
        maxDelegations: null,
        minLocalities: null,
        maxLocalities: null,
        createdAfter: null,
        createdBefore: null,
      }
      this.getGovernorates()
    },

    // Status toggle methods
    confirmToggleStatus(id, makeActive) {
      this.governorateToToggle = id
      this.newStatus = makeActive

      this.statusModalTitle = makeActive ? 'Activer le gouvernorat' : 'Désactiver le gouvernorat'
      this.statusModalMessage = makeActive
        ? 'Cette action permettra les livraisons dans cette région. Êtes-vous sûr?'
        : 'Cette action empêchera les livraisons dans cette région. Êtes-vous sûr?'

      this.$refs.confirmStatusModal.show()
    },

    cancelStatusChange() {
      this.governorateToToggle = null
      this.newStatus = null
    },

    handleStatusConfirmed() {
      if (this.governorateToToggle !== null) {
        this.toggleStatus(this.governorateToToggle, this.newStatus)
      }
    },

    async toggleStatus(id, makeActive) {
      try {
        this.toggleStatusLoading = id
        await axios.post(`/api/governorates/toggle-status/${id}/`, { is_active: makeActive })

        // Update the item in the governorates array to avoid a full reload
        const index = this.governorates.findIndex(governorate => governorate.id === id)
        if (index !== -1) {
          this.governorates[index].is_active = makeActive
        }

        this.showToast(
          'success',
          'top-center',
          `Le gouvernorat a été ${makeActive ? 'activé' : 'désactivé'} avec succès`,
        )
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du changement de statut')
      } finally {
        this.toggleStatusLoading = null
        this.governorateToToggle = null
        this.newStatus = null
      }
    },

    calculateStatistics() {
      this.activeCount = this.governorates.filter(g => g.is_active).length
      this.totalDelegations = this.governorates.reduce((sum, g) => sum + (g.num_delegations || 0), 0)
      this.totalLocalities = this.governorates.reduce((sum, g) => sum + (g.num_localities || 0), 0)
    },

    // toast
    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },

    // Remove pagination handlers for governorates
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
@import "@core/scss/vue/libs/vue-select.scss";

// Consistent form input and select field sizing
.form-control,
.filter-input,
.vs__dropdown-toggle {
  height: 40px;  // Set fixed height for all inputs
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:focus, &:active, &:hover {
    border-color: #114092;
    box-shadow: none;
  }
}

// Improve v-select styling to match regular inputs
.select-search-option {
  .vs__dropdown-toggle {
    padding: 5px 0; // Adjust padding to match regular inputs
    min-height: 40px; // Ensure minimum height
    display: flex;
    align-items: center;
  }

  .vs__selected-options {
    padding: 0 6px;
    align-items: center;
  }

  .vs__search {
    padding: 5px 8px;
    margin: 0;
  }

  .vs__actions {
    padding-top: 0;
    padding-bottom: 0;
  }

  .vs__clear {
    height: 22px;
  }

  .vs__open-indicator {
    height: 17px;
  }
}

// Enhanced table styles
.governorates-table {
  .vgt-table {
    border-collapse: separate;
    border-spacing: 0;

    th {
      position: relative;
      font-weight: 600;

      &.active-sorting {
        background-color: rgba($color: #7367f0, $alpha: 0.1);
        color: #7367f0;
      }

      &.sorting-header {
        cursor: pointer;

        &:hover {
          background-color: rgba($color: #7367f0, $alpha: 0.05);
        }
      }
    }

    td {
      vertical-align: middle;
    }
  }

  // Status badges
  .badge-glow {
    &.bg-success {
      box-shadow: 0 0 10px rgba(40, 199, 111, 0.6);
    }

    &.bg-danger {
      box-shadow: 0 0 10px rgba(234, 84, 85, 0.6);
    }
  }
}

// Badge styling to match the image
.badge-round {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.bg-light-primary {
  background-color: rgba($color: #7367f0, $alpha: 0.12) !important;
}

.bg-light-success {
  background-color: rgba($color: #28c76f, $alpha: 0.12) !important;
}

.bg-light-warning {
  background-color: rgba($color: #ff9f43, $alpha: 0.12) !important;
}

.bg-light-info {
  background-color: rgba($color: #00cfe8, $alpha: 0.12) !important;
}

.gov-text-purple {
  color: #7367f0 !important;
}

.btn-primary {
  background-color: #114092 !important;
}

// Animations
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

// Empty state styling
.empty-state {
  padding: 3rem 1rem;

  svg {
    opacity: 0.6;
  }
}

// Enhance filter section with consistent spacing
.card {
  .form-group {
    margin-bottom: 1rem;

    label {
      margin-bottom: 0.5rem;
      font-weight: 500;
    }
  }
}
</style>
